import React, { useState } from 'react';
import { UserTag, UserType } from '../UserTag/UserTag';
import { CalendarIcon, LocationMarkerIcon } from '../icons';
import { DefaultAvatar } from '../icons/DefaultAvatar';
import UserTypeInfo from '../UserTypeInfo/UserTypeInfo';
import './Comment.css';

export interface CommentEventInfo {
  artistName: string;
  artistAvatarUrl?: string;
  date: string;
  venue: string;
}

export interface CommentProps {
  /** User's display name */
  userName: string;
  /** URL for user's avatar image */
  avatarUrl?: string;
  /** Timestamp string (e.g., "Sept 16 3:29 PM") */
  timestamp: string;
  /** Array of user tag types to display as badges */
  userTags: UserType[];
  /** The comment text content */
  text: string;
  /** Whether to show the bottom event info row */
  showEventInfo?: boolean;
  /** Event information (required if showEventInfo is true) */
  eventInfo?: CommentEventInfo;
  /** Optional URL to navigate to user's profile when clicking name/avatar */
  userProfileUrl?: string;
  /** Optional click handler for user name/avatar (called when userProfileUrl is set) */
  onUserClick?: () => void;
  /** Optional additional click handler for user tags (called after opening info) */
  onTagClick?: (type: UserType) => void;
  /** Optional className for custom styling */
  className?: string;
}

export const Comment: React.FC<CommentProps> = ({
  userName,
  avatarUrl,
  timestamp,
  userTags,
  text,
  showEventInfo = false,
  eventInfo,
  userProfileUrl,
  onUserClick,
  onTagClick,
  className,
}) => {
  // Default to 'noAccount' tag when no user tags are provided
  const resolvedTags: UserType[] = userTags.length > 0 ? userTags : ['noAccount'];

  // State for UserTypeInfo overlays
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);

  const handleTagClick = (type: UserType) => {
    setOpenInfoType(type);
    onTagClick?.(type);
  };

  const handleInfoClose = () => {
    setOpenInfoType(null);
  };

  // Check if user profile is clickable (has account and profile URL)
  const isUserClickable = userProfileUrl && !(resolvedTags.length === 1 && resolvedTags[0] === 'noAccount');

  const handleUserClick = () => {
    if (isUserClickable) {
      onUserClick?.();
    }
  };
  return (
    <div className={`spec-comment ${className || ''}`}>
      {/* Top Info Section */}
      <div className="spec-comment__top-info">
        {/* Header Row: User Info + Timestamp */}
        <div className="spec-comment__header">
          <div
            className={`spec-comment__user-info${isUserClickable ? ' spec-comment__user-info--linkable' : ''}`}
            onClick={handleUserClick}
            style={isUserClickable ? { cursor: 'pointer' } : undefined}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={userName}
                className="spec-comment__avatar"
              />
            ) : (
              <div className="spec-comment__avatar">
                <DefaultAvatar size={14} />
              </div>
            )}
            <span className="spec-comment__user-name">{userName}</span>
          </div>
          <span className="spec-comment__timestamp">{timestamp}</span>
        </div>

        {/* Badges Row */}
        <div className="spec-comment__badges">
          {resolvedTags.map((tagType) => (
            <UserTag 
              key={tagType} 
              type={tagType} 
              onClick={() => handleTagClick(tagType)}
            />
          ))}
        </div>

        {/* Comment Text */}
        <p className="spec-comment__text">{text}</p>
      </div>

      {/* Event Info Row (conditionally rendered) */}
      {showEventInfo && eventInfo && (
        <div className="spec-comment__event-info">
          {/* Artist */}
          <div className="spec-comment__event-detail">
            {eventInfo.artistAvatarUrl ? (
              <img 
                src={eventInfo.artistAvatarUrl} 
                alt={eventInfo.artistName}
                className="spec-comment__event-avatar"
              />
            ) : (
              <div className="spec-comment__event-avatar spec-comment__event-avatar--empty" />
            )}
            <span className="spec-comment__event-text">{eventInfo.artistName}</span>
          </div>

          {/* Date */}
          <div className="spec-comment__event-detail">
            <CalendarIcon size="s" tone="darkGrey" />
            <span className="spec-comment__event-text">{eventInfo.date}</span>
          </div>

          {/* Venue */}
          <div className="spec-comment__event-detail">
            <LocationMarkerIcon size="s" tone="darkGrey" />
            <span className="spec-comment__event-text">{eventInfo.venue}</span>
          </div>
        </div>
      )}

      {/* UserTypeInfo overlays for each tag type */}
      {resolvedTags.map((tagType) => (
        <UserTypeInfo
          key={tagType}
          type={tagType}
          open={openInfoType === tagType}
          onClose={handleInfoClose}
        />
      ))}
    </div>
  );
};

export default Comment;
